import type { RefObject, WheelEvent } from 'react'
import type { CoversList } from 'blocks/Cards/Card.h'

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import * as Covers from 'blocks/Cards/BundleCard/BundleCardCovers'
import { CoversMap } from 'blocks/Cards/Card.h'
import { PosterState } from 'utils/constants'
import { stringToHash } from 'utils/helpers'
import { COLOR } from 'vars/color'

export function usePageVisibility() {
  const isDocumentAvailable = typeof document !== 'undefined'
  const [isVisible, setIsVisible] = useState(
    isDocumentAvailable ? document.visibilityState !== 'hidden' : false
  )

  const onVisibilityChange = () => {
    setIsVisible(document.visibilityState !== 'hidden')
  }

  useEffect(() => {
    document.addEventListener('visibilitychange', onVisibilityChange, false)

    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange)
  })

  return isVisible
}

/**
 * Resolves the element that actually scrolls the page: `.layout` when it is
 * styled as its own scroll container, otherwise the document (window).
 */
const getScrollElement = (): HTMLElement | null => {
  const layout = document.querySelector<HTMLElement>('.layout')
  if (!layout) return null
  const { overflowY } = window.getComputedStyle(layout)
  return overflowY === 'auto' || overflowY === 'scroll' ? layout : null
}

const getScrollTop = (): number => {
  if (typeof window === 'undefined') return 0
  return getScrollElement()?.scrollTop ?? window.scrollY
}

/**
 * usePageScrolled hook tracks whether the page has been scrolled from the top.
 * Returns true when scroll position > 0, false when at the top.
 * Uses requestAnimationFrame to limit updates to one per frame (~60fps).
 */
export const usePageScrolled = (): boolean => {
  const [isScrolled, setIsScrolled] = useState(() => getScrollTop() > 0)

  useEffect(() => {
    let ticking = false

    const handleScroll = (): void => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        setIsScrolled(getScrollTop() > 0)
        ticking = false
      })
    }

    handleScroll()
    const scrollTarget: EventTarget = getScrollElement() ?? window

    scrollTarget.addEventListener('scroll', handleScroll, { passive: true })
    return () =>
      scrollTarget.removeEventListener('scroll', handleScroll, {
        passive: true,
      } as EventListenerOptions)
  }, [])

  return isScrolled
}

/**
 * usePinnedBarBackground hook resolves the background token a pinned bar should use:
 * transparent at the top of the page, or the Header's background once scrolled,
 * so custom operator palettes are followed automatically.
 */
export const usePinnedBarBackground = (): COLOR => {
  const isScrolled = usePageScrolled()
  return isScrolled ? COLOR.BACKGROUND_DARK : COLOR.TRANSPARENT
}

/**
 * Locks the page's real scroll root while the component using it is mounted.
 *
 * `.layout` (`themeWrapperClassName`) is the scroll container when the app's CSS
 * gives it `overflow: auto|scroll` (see apps/tria/src/core/router/scroll.ts for
 * the same convention). Otherwise `document.documentElement` (`<html>`) is the
 * real scroll root here — `<body>` deliberately never gets its own overflow
 * (apps/tria/src/app/ui/ThemeProvider.css, `.tria-body-scroll-root`), so locking
 * `document.body` has no effect on page scroll (FP-4226).
 */
export const useLockPageScroll = (): void => {
  useEffect(() => {
    const target = getScrollElement() ?? document.documentElement
    const previousOverflow = target.style.overflow

    target.style.overflow = 'hidden'

    return () => {
      target.style.overflow = previousOverflow
    }
  }, [])
}

export const useSSRLazyLoading = () => {
  const [isShowComponent, setIsShowComponent] = useState(false)
  useEffect(() => {
    setIsShowComponent(true)
  }, [])

  return isShowComponent
}

interface IOptions {
  root?: Element
  rootMargin?: string
  threshold?: number | number[]
}

/**
 * useIntersectionObserver hook checks if the element is in the visibility area according to the options
 * @param ref to check
 * @param options
 * @returns boolean
 */
export const useIntersectionObserver = (
  ref: RefObject<Element | null>,
  { root, rootMargin, threshold }: IOptions = {},
  additionalKeys: any[] = []
): boolean => {
  const [isObservableElementVisible, setVisibility] = useState(false)

  const callback = (entries: IntersectionObserverEntry[]): void => {
    setVisibility(
      entries.some(
        (entry) => entry.isIntersecting && entry.intersectionRatio > 0
      )
    )
  }

  useEffect(() => {
    const element = ref?.current
    const supports = !!window.IntersectionObserver

    if (!supports || !element) return

    const observer = new IntersectionObserver(callback, {
      root,
      rootMargin,
      threshold,
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [ref, ref?.current, root, rootMargin, threshold, ...additionalKeys])

  return isObservableElementVisible
}

type Callback = () => void
/**
 * Hook useBooleanState is used to change the boolean state
 * @param bool initial value
 */
export const useBooleanState = (
  bool: boolean
): [boolean, Callback, Callback, Callback] => {
  const [state, setState] = useState(bool)

  const setTrue = useCallback((): void => setState(true), [])
  const setFalse = useCallback((): void => setState(false), [])
  const toggle = useCallback((): void => setState((state) => !state), [])

  return [state, setTrue, setFalse, toggle]
}

/**
 * UseWindowResize hook is used to call handler on window resize event
 * @param handler
 */
export const useWindowResize = (handler: () => void): void => {
  useEffect(() => {
    window.addEventListener('resize', handler)
    return () => {
      window.removeEventListener('resize', handler)
    }
  }, [])
}

export const useClientWidth = (): number => {
  const [clientWidth, setClientWidth] = useState(window.innerWidth)

  useEffect(() => {
    const updateClientWidth = () => {
      setClientWidth(window.innerWidth)
    }

    setClientWidth(window.innerWidth)

    window.addEventListener('resize', updateClientWidth)
    return () => window.removeEventListener('resize', updateClientWidth)
  }, [])

  return clientWidth
}

export const useClientResolution = () => {
  const [clientResolution, setClientResolution] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  })

  useEffect(() => {
    const updateClientResolution = () => {
      setClientResolution({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    window.addEventListener('resize', updateClientResolution)
    return () => window.removeEventListener('resize', updateClientResolution)
  }, [])

  return clientResolution
}

export const useOrientation = () => {
  const [state, setState] = useState(() => {
    const orientation = window.innerWidth > window.innerHeight ? 90 : 0
    return {
      isPortrait: orientation === 0,
      isLandscape: orientation === 90,
      orientation: orientation === 0 ? 'portrait' : 'landscape',
    }
  })
  const handleOrientationChange = useCallback(() => {
    const orientation = window.innerWidth > window.innerHeight ? 90 : 0
    const next = {
      isPortrait: orientation === 0,
      isLandscape: orientation === 90,
      orientation: orientation === 0 ? 'portrait' : 'landscape',
    }
    if (state.orientation !== next.orientation) setState(next)
  }, [state.orientation])
  useEffect(() => {
    if (typeof window !== 'undefined') {
      handleOrientationChange()
      window.addEventListener('load', handleOrientationChange, false)
      window.addEventListener('resize', handleOrientationChange, false)
    }
    return () => {
      window.removeEventListener('resize', handleOrientationChange, false)
      window.removeEventListener('load', handleOrientationChange, false)
    }
  }, [handleOrientationChange])
  return state
}

export type VisualViewportRect = {
  top: number
  left: number
  width: number
  height: number
}

/** Shrink of visualViewport vs layout height that usually means the OSK is open. */
const KEYBOARD_HEIGHT_THRESHOLD_PX = 150

const getVisualViewportRect = (): VisualViewportRect => {
  const vv = window.visualViewport
  if (!vv) {
    return {
      top: 0,
      left: 0,
      width: window.innerWidth,
      height: window.innerHeight,
    }
  }

  return {
    top: vv.offsetTop,
    left: vv.offsetLeft,
    width: vv.width,
    height: vv.height,
  }
}

const isKeyboardLikelyOpenFor = (
  rect: VisualViewportRect,
  layoutHeight: number
): boolean =>
  rect.height < layoutHeight - KEYBOARD_HEIGHT_THRESHOLD_PX || rect.top > 0

/**
 * Tracks the visible viewport (e.g. when the on-screen keyboard shrinks it on mobile).
 */
export const useVisualViewportRect = (): {
  rect: VisualViewportRect
  isKeyboardLikelyOpen: boolean
} => {
  const [state, setState] = useState(() => {
    const rect = getVisualViewportRect()
    const layoutHeight = window.innerHeight
    return {
      rect,
      layoutHeight,
      isKeyboardLikelyOpen: isKeyboardLikelyOpenFor(rect, layoutHeight),
    }
  })

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const update = () => {
      const rect = getVisualViewportRect()
      const layoutHeight = window.innerHeight
      setState({
        rect,
        layoutHeight,
        isKeyboardLikelyOpen: isKeyboardLikelyOpenFor(rect, layoutHeight),
      })
    }

    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    window.addEventListener('orientationchange', update)

    update()

    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      window.removeEventListener('orientationchange', update)
    }
  }, [])

  return {
    rect: state.rect,
    isKeyboardLikelyOpen: state.isKeyboardLikelyOpen,
  }
}

/**
 * Scrolls the focused control into view when the OSK is open, only if focus is inside container.
 * Debounced so visualViewport resize bursts (keyboard animation / rotation) do not spam scroll.
 */
export const useScrollFocusedIntoViewOnKeyboard = (
  containerRef: RefObject<HTMLElement | null>,
  isKeyboardLikelyOpen: boolean,
  visibleHeight: number
): void => {
  useEffect(() => {
    if (!isKeyboardLikelyOpen) return

    const timeoutId = window.setTimeout(() => {
      const activeElement = document.activeElement
      if (!(activeElement instanceof HTMLElement)) return
      if (!containerRef.current?.contains(activeElement)) return

      activeElement.scrollIntoView({ block: 'center', inline: 'nearest' })
    }, 100)

    return () => window.clearTimeout(timeoutId)
  }, [containerRef, isKeyboardLikelyOpen, visibleHeight])
}

/**
 * useCountdown hook is used to determine the remaining time before the event
 * @param targetDate
 * @param onCompleted called once, on the tick that crosses `targetDate`
 * @returns object
 */

export const useCountdown = (targetDate: string, onCompleted?: () => void) => {
  const countDownDate = new Date(targetDate).getTime()

  const [countDown, setCountDown] = useState(
    countDownDate - new Date().getTime()
  )

  const onCompletedRef = useRef(onCompleted)
  useEffect(() => {
    onCompletedRef.current = onCompleted
  }, [onCompleted])

  useEffect(() => {
    const remaining = countDownDate - new Date().getTime()
    setCountDown(remaining)

    // The same tick that updates the display reports completion, so a
    // countdown never runs a second timer alongside this interval.
    let completed = remaining <= 0
    const interval = setInterval(() => {
      const left = countDownDate - new Date().getTime()
      setCountDown(left)

      if (!completed && left <= 0) {
        completed = true
        onCompletedRef.current?.()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [countDownDate])

  const getCountdownValues = (countDown: number) => {
    const minuteInMilliseconds = 1000 * 60
    const hourInMilliseconds = minuteInMilliseconds * 60
    const dayInMilliseconds = hourInMilliseconds * 24
    const days = Math.floor(countDown / dayInMilliseconds)
    const hours = Math.floor(
      (countDown % dayInMilliseconds) / hourInMilliseconds
    )
    const minutes = Math.floor(
      (countDown % hourInMilliseconds) / minuteInMilliseconds
    )
    const seconds = Math.floor((countDown % minuteInMilliseconds) / 1000)

    return { days, hours, minutes, seconds }
  }

  return getCountdownValues(countDown)
}

export const useMediaQuery = (query: string, defaults: boolean): boolean => {
  const supportMatchMedia =
    typeof window !== 'undefined' && //
    typeof window.matchMedia !== 'undefined'

  const [match, setMatch] = React.useState(() =>
    supportMatchMedia //
      ? window.matchMedia(query).matches
      : defaults
  )

  useEffect(() => {
    if (!supportMatchMedia) return

    const media = window.matchMedia(query)
    const onChange = (e: MediaQueryListEvent): void => setMatch(e.matches)

    // old deprecated method for older browsers
    // https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList/addListener
    if (media.addListener) {
      media.addListener(onChange)
    } else {
      media.addEventListener('change', onChange)
    }

    return () => {
      // old deprecated method for older browsers
      // https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList/removeListener
      if (media.removeListener) {
        media.removeListener(onChange)
      } else {
        media.removeEventListener('change', onChange)
      }
    }
  }, [query, supportMatchMedia])

  return match
}

export const useTablet = (): boolean =>
  useMediaQuery(
    '(hover: none) and (pointer: coarse), (hover: none) and (pointer: fine)',
    false
  )

/**
 * usePoster hook is used to set cover logo for bundles
 * @param title
 * @param poster
 * @returns any
 */

export const usePoster = (title: string, poster?: CoversList) => {
  const getPoster = (title: string, listLength: number) => {
    const subCategoryNameHash = stringToHash(title)
    const itemNumber = Math.abs(subCategoryNameHash) % listLength
    const item =
      itemNumber > 0
        ? itemNumber <= listLength + 1
          ? itemNumber
          : listLength + 1
        : 1
    return `Cover${item}`
  }
  const img = getPoster(title, Object.keys(Covers).length)

  return poster ? CoversMap[poster] : CoversMap[img]
}

/**
 * useHover hook is used to set hover to ref object
 * @param ref
 * @returns value
 */

export const useHover = <T extends HTMLElement = HTMLElement>(
  elementRef: RefObject<T | null>
): boolean => {
  const [value, setValue] = useState<boolean>(false)

  const handleMouseOver = () => setValue(true)

  const handleMouseLeave = () => setValue(false)

  useEffect(() => {
    const ref = elementRef.current!
    if (ref) {
      ref.addEventListener('mouseover', handleMouseOver)
      ref.addEventListener('mouseleave', handleMouseLeave)
      return () => {
        ref.removeEventListener('mouseleave', handleMouseLeave)
        ref.removeEventListener('mouseover', handleMouseOver)
      }
    }
  })

  return value
}

const ScrollEvent = {
  Keydown: 'keydown',
  Wheel: 'wheel',
} as const
type ScrollEvent = (typeof ScrollEvent)[keyof typeof ScrollEvent]

export const useScrolling = (
  overlayRef: RefObject<HTMLDivElement | null>
): void => {
  //keyboard keys affecting scrolling
  const keys = { 37: 'left', 38: 'up', 39: 'right', 40: 'down' }

  const getScrollParent = (node: Element | null): Element | null => {
    if (node === null) {
      return null
    }

    if (node.scrollHeight > node.clientHeight) {
      return node
    }

    return getScrollParent(node.parentNode as Element)
  }

  const isElementOverlay = (
    e: Event & KeyboardEvent & WheelEvent<HTMLDivElement>
  ): boolean => {
    const target = e.target as Element
    return overlayRef.current === target
  }

  const needToLockScrolling = (
    e: Event & KeyboardEvent & WheelEvent<HTMLDivElement>
  ): boolean => {
    const target = e.target as Element

    const scrollableElement = getScrollParent(target)

    const isLayout =
      scrollableElement !== null &&
      scrollableElement.classList.contains('layout')

    if (isLayout) return true

    if (scrollableElement) {
      const { scrollHeight, clientHeight, scrollTop } = scrollableElement
      const { deltaY } = e
      const isDeltaPositive = deltaY > 0

      //Sometimes scrollHeight is not equal to clientHeight + scrollTop. Therefore, we will add additional pixels to prevent scrolling.
      const extraPixels = 5
      const isScrollable = scrollHeight > clientHeight + scrollTop + extraPixels

      if (e.type === ScrollEvent.Wheel)
        return (
          (!isScrollable && isDeltaPositive) || (!scrollTop && !isDeltaPositive)
        )
      if (e.type === ScrollEvent.Keydown) return true
    }

    return false
  }

  const preventDefault = useCallback(
    (e: Event & KeyboardEvent & WheelEvent<HTMLDivElement>): void => {
      if (isElementOverlay(e) || needToLockScrolling(e)) e.preventDefault()
    },
    []
  )

  const preventDefaultForScrollKeys = useCallback(
    (e: Event & KeyboardEvent & WheelEvent<HTMLDivElement>): void | boolean => {
      if (keys[e.keyCode] && (isElementOverlay(e) || needToLockScrolling(e))) {
        preventDefault(e)
        return false
      }
    },
    []
  )

  // modern Chrome requires { passive: false } when adding event
  let supportsPassive = false
  try {
    // prettier-ignore
    // @ts-ignore
    window.addEventListener('supportsPassive', null,
      Object.defineProperty({}, 'passive', {
        get: function () {
          supportsPassive = true
        },
      })
    )
  } catch (ignore) {
    // do nothing
  }

  const wheelOpt = supportsPassive ? { passive: false } : false
  const wheelEvent =
    'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel'

  const disableScroll = (): void => {
    // @ts-ignore
    window.addEventListener('DOMMouseScroll', preventDefault, false)
    // @ts-ignore
    window.addEventListener(wheelEvent, preventDefault, wheelOpt)
    // @ts-ignore
    window.addEventListener('touchmove', preventDefault, wheelOpt)
    // @ts-ignore
    window.addEventListener('keydown', preventDefaultForScrollKeys, false)
  }

  const enableScroll = (): void => {
    // @ts-ignore
    window.removeEventListener('DOMMouseScroll', preventDefault, false)
    // @ts-ignore
    window.removeEventListener(wheelEvent, preventDefault, wheelOpt)
    // @ts-ignore
    window.removeEventListener('touchmove', preventDefault, wheelOpt)
    // @ts-ignore
    window.removeEventListener('keydown', preventDefaultForScrollKeys, false)
  }

  useLayoutEffect(() => {
    disableScroll()
    return enableScroll
  }, [])
}

interface IUseLoadingImageParams {
  handleLoading?: () => void
  handleError?: () => void
  poster: RefObject<HTMLImageElement | null>
}
interface IUseLoadingImage {
  state: number
  onLoad: () => void
  onError: () => void
}
export const useLoadingImage = ({
  handleLoading,
  handleError,
  poster,
}: IUseLoadingImageParams): IUseLoadingImage => {
  const [state, setState] = useState<number>(PosterState.Loading)

  const onLoad = useCallback(() => {
    if (!poster.current) return

    setState(PosterState.Idle)
    handleLoading?.()
  }, [handleLoading])

  const onError = useCallback(() => {
    if (!poster.current) return

    setState(PosterState.Error)
    handleError?.()
  }, [handleError])

  return { state, onLoad, onError }
}

export const useLazyLoading = ({
  ref,
  posterUrl,
}: {
  ref: RefObject<HTMLImageElement | null> | null
  posterUrl: string | undefined
}) => {
  useEffect(() => {
    const imgElement = ref?.current
    if (!imgElement || !posterUrl) return

    const observerOptions: IntersectionObserverInit = {
      rootMargin: '0px 0px 300px 0px',
      threshold: 0.1,
    }

    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const image = entry.target as HTMLImageElement

          if (image.dataset.src) {
            image.src = image.dataset.src
          }

          imageObserver.unobserve(image)
        }
      })
    }, observerOptions)

    imageObserver.observe(imgElement)

    return () => {
      imageObserver.unobserve(imgElement)
    }
  }, [posterUrl])
}

export const useIsClientDesktop = () => {
  if (typeof document !== 'undefined' && typeof window !== 'undefined') {
    // oxlint-disable-next-line eslint-plugin-react-hooks/rules-of-hooks
    const { width } = useClientResolution()
    const isTablet = width < 1240
    return !isTablet
  }
}

/**
 * usePropForDesktopEmptyString hook for desktop accepts string return empty string
 * @param prop
 */
export const usePropForDesktopEmptyString = (prop: string | undefined) => {
  return useIsClientDesktop() ? '' : prop
}

/**
 * useScrollbarAppearance hook tracks the moment when the scrollbar appears on the page
 */
export const useScrollbarAppearance = (
  onScrollbarAppear?: () => void
): {
  hasScrollbar: boolean
} => {
  const [hasScrollbar, setHasScrollbar] = useState(false)
  const hasTriggeredRef = useRef(false)

  const checkScrollbar = useCallback(() => {
    if (typeof document === 'undefined') return

    const body = document.body
    const html = document.documentElement

    const currentScrollHeight = Math.max(
      body.scrollHeight,
      html.scrollHeight,
      body.offsetHeight,
      html.offsetHeight,
      body.clientHeight,
      html.clientHeight
    )
    const currentClientHeight = Math.max(
      window.innerHeight,
      html.clientHeight,
      body.clientHeight
    )

    const scrollbarExists = currentScrollHeight > currentClientHeight

    if (scrollbarExists && !hasScrollbar && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true
      setHasScrollbar(true)
      onScrollbarAppear?.()
    } else if (scrollbarExists) {
      setHasScrollbar(true)
    } else {
      setHasScrollbar(false)
    }
  }, [hasScrollbar, onScrollbarAppear])

  useEffect(() => {
    checkScrollbar()

    const resizeObserver = new ResizeObserver(() => {
      checkScrollbar()
    })

    if (document.body) {
      resizeObserver.observe(document.body)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [checkScrollbar])

  return { hasScrollbar }
}

/**
 * Calls `onOutside` when `mousedown` or `touchstart` happens outside `ref.current`.
 */
export const usePointerDownOutside = (
  ref: RefObject<Element | null>,
  onOutside: () => void,
  enabled = true
): void => {
  useEffect(() => {
    if (!enabled) return

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const el = ref.current
      if (!el) return
      if (e.target instanceof Node && !el.contains(e.target)) {
        onOutside()
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [ref, onOutside, enabled])
}
